import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import tw from 'twin.macro'
import BN from 'bignumber.js'
import { ChainId, Currency, CurrencyAmount, JSBI, Token, Trade, TradeType } from '@nominex/nomiswap-sdk'
import { SwapVertIcon, useModal } from '@nominex/dex-uikit'
import { SlButton, SlIcon } from '@shoelace-style/shoelace/dist/react'

import { useTranslation } from 'contexts/Localization'
import { INITIAL_ALLOWED_SLIPPAGE } from 'config/constants'

import { useWagmi } from 'libs/wagmi'
import { useAppKitNetwork } from '@reown/appkit/react'
import { useIsTransactionUnsupported } from 'hooks/Trades'
import { useAllTokens, useCurrency } from 'hooks/Tokens'
import { ApprovalState, useApproveCallbackFromTrade } from 'hooks/useApproveCallback'
import { useSwapCallback } from 'hooks/useSwapCallback'
import useWrapCallback, { WrapType } from 'hooks/useWrapCallback'

import { Field } from 'state/swap/actions'
import { useDefaultsFromURLSearch, useDerivedSwapInfo, useSwapActionHandlers, useSwapState } from 'state/swap/hooks'
import { useExpertModeManager, useUserSingleHopOnly, useUserSlippageTolerance } from 'state/user/hooks'
import { useFarms, usePollFarmsPublicData } from 'state/farms/hooks'

import { maxAmountSpend } from 'utils/maxAmountSpend'
import { computeTradePriceBreakdown, warningSeverity } from 'utils/prices'
import { getCurrencyPrice } from 'utils/currency'
import { logAmplitudeEvent, logGtmEvent } from 'utils/analytics'

import { infoApi } from 'restApi'
import { CashbackPoolResponse } from 'restApi/openapi/models'

import { GreyCard } from 'components/Card'
import { AutoColumn, Column, RowBetween } from 'components/Layout'
import CurrencySelectionPanel from 'components/CurrencySelectionPanel'
import ProgressSteps from 'components/App/ProgressSteps'
import { AppBody, AppTitle } from 'components/App'
import ConnectWalletButton from 'components/ConnectWalletButton'
import UnsupportedCurrencyFooter from 'components/UnsupportedCurrencyFooter'
import AnimatedIcon from 'components/AnimatedIcon'

import AddressInputPanel from './components/AddressInputPanel'
import ConfirmSwapModal from './components/ConfirmSwapModal'
import ExpertModeButton from './components/ExpertModeButton'
import AdvancedSwapDetailsDropdown from './components/AdvancedSwapDetailsDropdown'
import confirmPriceImpactWithoutFee from './components/confirmPriceImpactWithoutFee'
import { Wrapper } from './components/styles'
import CashbackWidget from './components/CashbackWidget'
import CashbackPoolWidget from './components/CashbackPoolWidget'
import BinanceBridgeWidget from './components/BinanceBridgeWidget'
import TradePrice from './components/TradePrice'
import ImportTokenWarningModal from './components/ImportTokenWarningModal'

import Page from '../Page'

const Label = tw.div`
  text-14 text-dark-gray-100
`

const StyledSwitchControlsWrapper = tw.div`
  flex justify-center items-center absolute w-full translate-y-[-50%] z-10
`

export default function Swap({ history }: RouteComponentProps) {
  const { t } = useTranslation()
  const { account, chain, chainId } = useWagmi()
  const loadedUrlParams = useDefaultsFromURLSearch()

  usePollFarmsPublicData()

  // token warning stuff
  const [loadedInputCurrency, loadedOutputCurrency] = [
    useCurrency(loadedUrlParams?.inputCurrencyId),
    useCurrency(loadedUrlParams?.outputCurrencyId),
  ]
  const urlLoadedTokens: Token[] = useMemo(
    () => [loadedInputCurrency, loadedOutputCurrency]?.filter((c): c is Token => c?.isToken) ?? [],
    [loadedInputCurrency, loadedOutputCurrency],
  )

  // dismiss warning if all imported tokens are in active lists
  const defaultTokens = useAllTokens()
  const importTokensNotInDefault =
    urlLoadedTokens && urlLoadedTokens.filter((token: Token) => !(token.address in defaultTokens))

  // for expert mode
  const [isExpertMode] = useExpertModeManager()

  // get custom setting values for user
  const [allowedSlippage] = useUserSlippageTolerance()

  // swap state
  const { independentField, typedValue, recipient } = useSwapState()
  const { v2Trade, currencyBalances, parsedAmount, currencies, inputError: swapInputError } = useDerivedSwapInfo()

  const {
    wrapType,
    execute: onWrap,
    inputError: wrapInputError,
  } = useWrapCallback(currencies[Field.INPUT], currencies[Field.OUTPUT], typedValue)
  const showWrap: boolean = wrapType !== WrapType.NOT_APPLICABLE
  const trade = showWrap ? undefined : v2Trade

  const { data: farms } = useFarms()

  const parsedAmounts = showWrap
    ? {
        [Field.INPUT]: parsedAmount,
        [Field.OUTPUT]: parsedAmount,
      }
    : {
        [Field.INPUT]: independentField === Field.INPUT ? parsedAmount : trade?.inputAmount,
        [Field.OUTPUT]: independentField === Field.OUTPUT ? parsedAmount : trade?.outputAmount,
      }

  const { onSwitchTokens, onCurrencySelection, onUserInput, onChangeRecipient } = useSwapActionHandlers()
  const isValid = !swapInputError
  const dependentField: Field = independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT

  const handleTypeInput = useCallback(
    (value: string) => {
      onUserInput(Field.INPUT, value)
    },
    [onUserInput],
  )
  const handleTypeOutput = useCallback(
    (value: string) => {
      onUserInput(Field.OUTPUT, value)
    },
    [onUserInput],
  )

  // modal and loading
  const [{ tradeToConfirm, attemptingTxn, txHash }, setSwapState] = useState<{
    tradeToConfirm: Trade<Currency, Currency, TradeType> | undefined
    attemptingTxn: boolean
    txHash: string | undefined
  }>({
    tradeToConfirm: undefined,
    attemptingTxn: false,
    txHash: undefined,
  })

  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: showWrap
      ? parsedAmounts[independentField]?.toExact() ?? ''
      : parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  const route = trade?.route
  const userHasSpecifiedInputOutput = Boolean(
    currencies[Field.INPUT] && currencies[Field.OUTPUT] && parsedAmounts[independentField]?.greaterThan(JSBI.BigInt(0)),
  )
  const noRoute = !route

  // check whether the user has approved the router on the input token
  const [approval, approveCallback] = useApproveCallbackFromTrade(trade, allowedSlippage, chainId)

  // check if user has gone through approval process, used to show two step buttons, reset on token change
  const [approvalSubmitted, setApprovalSubmitted] = useState<boolean>(false)

  // mark when a user has submitted an approval, reset onTokenSelection for input field
  useEffect(() => {
    if (approval === ApprovalState.PENDING) {
      setApprovalSubmitted(true)
    }
  }, [approval, approvalSubmitted])

  const maxAmountInput: CurrencyAmount<Currency> | undefined = maxAmountSpend(currencyBalances[Field.INPUT])
  const atMaxAmountInput = Boolean(maxAmountInput && parsedAmounts[Field.INPUT]?.equalTo(maxAmountInput))

  // the callback to execute the swap
  const { callback: swapCallback, error: swapCallbackError } = useSwapCallback(trade, allowedSlippage, recipient)

  const { priceImpactWithoutFee, realizedTradeFee, realizedLpFee, tradeFee } = computeTradePriceBreakdown(trade)

  const [singleHopOnly] = useUserSingleHopOnly()

  const handleSwap = useCallback(() => {
    if (priceImpactWithoutFee && !confirmPriceImpactWithoutFee(priceImpactWithoutFee, t)) {
      return
    }
    if (!swapCallback) {
      return
    }
    setSwapState({ attemptingTxn: true, tradeToConfirm, txHash: undefined })
    swapCallback()
      .then((hash) => {
        const volumeInUsdt = new BN(tradeToConfirm.outputAmount.quotient.toString())
          .div(10 ** tradeToConfirm.outputAmount.currency.decimals)
          .times(getCurrencyPrice(tradeToConfirm.outputAmount.currency, farms))
          .toFixed(2)

        const poolName = [tradeToConfirm.outputAmount.currency.symbol, tradeToConfirm.inputAmount.currency.symbol]
          .sort()
          .join('-')

        logAmplitudeEvent('DefiSwap', {
          volume_in_usdt: volumeInUsdt,
          pool: poolName,
          website: 'dex',
        })
        logGtmEvent({ event: 'DefiSwap' })

        setSwapState({ attemptingTxn: false, tradeToConfirm, txHash: hash })
      })
      .catch(() => {
        setSwapState({
          attemptingTxn: false,
          tradeToConfirm,
          txHash: undefined,
        })
      })
  }, [priceImpactWithoutFee, swapCallback, tradeToConfirm, farms, t])

  // errors
  const [showInverted, setShowInverted] = useState<boolean>(false)

  // warnings on slippage
  const priceImpactSeverity = warningSeverity(priceImpactWithoutFee)

  // show approve flow when: no error on inputs, not approved or pending, or approved in current session
  // never show if price impact is above threshold in non expert mode
  const showApproveFlow =
    !swapInputError &&
    (approval === ApprovalState.NOT_APPROVED ||
      approval === ApprovalState.PENDING ||
      (approvalSubmitted && approval === ApprovalState.APPROVED)) &&
    !(priceImpactSeverity > 3 && !isExpertMode)

  const handleConfirmDismiss = useCallback(() => {
    setSwapState({ tradeToConfirm, attemptingTxn, txHash })
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onUserInput(Field.INPUT, '')
    }
  }, [attemptingTxn, onUserInput, tradeToConfirm, txHash])

  const handleAcceptChanges = useCallback(() => {
    setSwapState({ tradeToConfirm: trade, txHash, attemptingTxn })
  }, [attemptingTxn, trade, txHash])

  const handleInputSelect = useCallback(
    (inputCurrency) => {
      setApprovalSubmitted(false) // reset 2 step UI for approvals
      onCurrencySelection(Field.INPUT, inputCurrency)
    },
    [onCurrencySelection],
  )

  const handleMaxInput = useCallback(() => {
    if (maxAmountInput) {
      onUserInput(Field.INPUT, maxAmountInput.toExact())
    }
  }, [maxAmountInput, onUserInput])

  const handleOutputSelect = useCallback(
    (outputCurrency) => {
      onCurrencySelection(Field.OUTPUT, outputCurrency)
    },
    [onCurrencySelection],
  )

  const swapIsUnsupported = useIsTransactionUnsupported(currencies?.INPUT, currencies?.OUTPUT)

  const [onPresentImportTokenWarningModal] = useModal(
    <ImportTokenWarningModal tokens={importTokensNotInDefault} onCancel={() => history.push('/swap/')} />,
  )

  useEffect(() => {
    if (importTokensNotInDefault.length > 0) {
      onPresentImportTokenWarningModal()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importTokensNotInDefault.length])

  const [cashbackPool, setCashbackPoolState] = useState<CashbackPoolResponse>({
    distributed: 0,
    total: 0,
  })

  const [onPresentConfirmModal] = useModal(
    <ConfirmSwapModal
      trade={trade}
      originalTrade={tradeToConfirm}
      onAcceptChanges={handleAcceptChanges}
      attemptingTxn={attemptingTxn}
      txHash={txHash}
      cashbackPool={cashbackPool}
      recipient={recipient}
      allowedSlippage={allowedSlippage}
      onConfirm={handleSwap}
      customOnDismiss={handleConfirmDismiss}
    />,
    true,
    true,
    'confirmSwapModal',
  )

  useEffect(() => {
    const fetchCashbackPool = async () => {
      try {
        const response = await infoApi.getCashbackPool()
        setCashbackPoolState(response)
      } catch (e: any) {
        e.json().then(console.error)
      }
    }

    fetchCashbackPool()

    const pollingInterval = setInterval(fetchCashbackPool, 5000)

    return () => clearInterval(pollingInterval)
  }, [])

  return (
    <Page>
      <AppTitle
        className="mb-20"
        title={t('Make a Swap with Cashback')}
        subtitle={t(
          'Up to 100% of the trading fees is returned in NMX token. Percentage of cashback depends on your farming level.',
        )}
        alignItems="center"
        style={{ textAlign: 'center' }}
        maxWidth="555px"
      />

      {chain?.id === ChainId.BSC && <CashbackWidget />}

      <AppBody>
        <Wrapper id="swap-page">
          <div className="flex flex-col">
            <CurrencySelectionPanel
              label={independentField === Field.OUTPUT && !showWrap && trade ? t('From (estimated)') : t('From')}
              value={formattedAmounts[Field.INPUT]}
              showMaxButton={!atMaxAmountInput}
              currency={currencies[Field.INPUT]}
              showCommonBases
              onUserInput={handleTypeInput}
              onMax={handleMaxInput}
              onCurrencySelect={handleInputSelect}
              id="swap-currency-input"
            />
            <div className="relative my-8">
              <StyledSwitchControlsWrapper>
                <AnimatedIcon
                  my="8px"
                  clickable
                  onClick={() => {
                    setApprovalSubmitted(false) // reset 2 step UI for approvals
                    onSwitchTokens()
                  }}
                >
                  <SwapVertIcon width="16px" />
                </AnimatedIcon>
                {recipient === null && !showWrap && isExpertMode ? (
                  <ExpertModeButton
                    className="ml-8"
                    text={t('Add a send (optional)')}
                    id="add-recipient-button"
                    onClick={() => onChangeRecipient('')}
                  >
                    <SlIcon name="plus" library="unicons" className="square-16" />
                  </ExpertModeButton>
                ) : null}
              </StyledSwitchControlsWrapper>
            </div>
            <CurrencySelectionPanel
              value={formattedAmounts[Field.OUTPUT]}
              onUserInput={handleTypeOutput}
              label={independentField === Field.INPUT && !showWrap && trade ? t('To (estimated)') : t('To')}
              showMaxButton={false}
              showCommonBases
              currency={currencies[Field.OUTPUT]}
              onCurrencySelect={handleOutputSelect}
              id="swap-currency-output"
            />

            {isExpertMode && recipient !== null && !showWrap ? (
              <>
                <div className="relative my-8">
                  <StyledSwitchControlsWrapper>
                    <ExpertModeButton
                      className="ml-8"
                      text={t('Remove a send')}
                      id="remove-recipient-button"
                      onClick={() => onChangeRecipient(null)}
                    >
                      <SlIcon name="minus-circle" library="unicons" className="square-16" />
                    </ExpertModeButton>
                  </StyledSwitchControlsWrapper>
                </div>
                <AddressInputPanel id="recipient" value={recipient} onChange={onChangeRecipient} />
              </>
            ) : null}

            {showWrap ? null : (
              <AutoColumn gap="8px" style={{ marginTop: '14px' }}>
                {Boolean(trade) && (
                  <RowBetween align="center">
                    <Label>{t('Price')}</Label>
                    <TradePrice
                      price={trade?.executionPrice}
                      showInverted={showInverted}
                      setShowInverted={setShowInverted}
                    />
                  </RowBetween>
                )}
              </AutoColumn>
            )}

            {allowedSlippage !== INITIAL_ALLOWED_SLIPPAGE && (
              <RowBetween align="center">
                <Label>{t('Slippage Tolerance')}</Label>
                <div className="font-semibold text-blue text-14">{allowedSlippage / 100}%</div>
              </RowBetween>
            )}
          </div>
          <div className="mt-16">
            {swapIsUnsupported ? (
              <SlButton variant="primary" disabled className="mb-4 w-[100%]">
                {t('Unsupported Asset')}
              </SlButton>
            ) : !account ? (
              <ConnectWalletButton className="w-[100%]" />
            ) : showWrap ? (
              <SlButton variant="primary" size="large" disabled={Boolean(wrapInputError)} onClick={onWrap}>
                {wrapInputError ??
                  (wrapType === WrapType.WRAP ? 'Wrap' : wrapType === WrapType.UNWRAP ? 'Unwrap' : null)}
              </SlButton>
            ) : noRoute && userHasSpecifiedInputOutput ? (
              <GreyCard style={{ textAlign: 'center' }}>
                <div className="mb-4 text-dark-gray-100">{t('Insufficient liquidity for this trade.')}</div>
                {singleHopOnly && <div className="mb-4 text-dark-gray-100">{t('Try enabling multi-hop trades.')}</div>}
              </GreyCard>
            ) : showApproveFlow ? (
              <RowBetween>
                <SlButton
                  size="large"
                  className="w-[48%]"
                  variant={approval === ApprovalState.APPROVED ? 'success' : 'primary'}
                  onClick={approveCallback}
                  disabled={approval !== ApprovalState.NOT_APPROVED || approvalSubmitted}
                  loading={approval === ApprovalState.PENDING}
                >
                  {approvalSubmitted && approval === ApprovalState.APPROVED
                    ? t('Enabled')
                    : t('Enable %asset%', { asset: currencies[Field.INPUT]?.symbol ?? '' })}
                </SlButton>
                <SlButton
                  size="large"
                  className="w-[48%]"
                  variant={isValid && priceImpactSeverity > 2 ? 'danger' : 'primary'}
                  onClick={() => {
                    if (isExpertMode) {
                      handleSwap()
                    } else {
                      setSwapState({
                        tradeToConfirm: trade,
                        attemptingTxn: false,
                        txHash: undefined,
                      })
                      onPresentConfirmModal()
                    }
                  }}
                  id="swap-button"
                  disabled={
                    !isValid || approval !== ApprovalState.APPROVED || (priceImpactSeverity > 3 && !isExpertMode)
                  }
                >
                  {priceImpactSeverity > 3 && !isExpertMode
                    ? t('Price Impact High')
                    : priceImpactSeverity > 2
                      ? t('Swap Anyway')
                      : t('Swap')}
                </SlButton>
              </RowBetween>
            ) : (
              <SlButton
                size="large"
                className="w-[100%]"
                variant={isValid && priceImpactSeverity > 2 && !swapCallbackError ? 'danger' : 'primary'}
                onClick={() => {
                  if (isExpertMode) {
                    handleSwap()
                  } else {
                    setSwapState({
                      tradeToConfirm: trade,
                      attemptingTxn: false,
                      txHash: undefined,
                    })
                    onPresentConfirmModal()
                  }
                }}
                id="swap-button"
                disabled={!isValid || (priceImpactSeverity > 3 && !isExpertMode) || !!swapCallbackError}
              >
                {swapInputError ||
                  (priceImpactSeverity > 3 && !isExpertMode
                    ? t('Price Impact Too High')
                    : priceImpactSeverity > 2
                      ? t('Swap Anyway')
                      : t('Swap'))}
              </SlButton>
            )}
            {showApproveFlow && (
              <Column style={{ marginTop: '1rem' }}>
                <ProgressSteps isFirstStepCompleted={approval === ApprovalState.APPROVED} />
              </Column>
            )}
            {!swapIsUnsupported ? (
              trade && (
                <AdvancedSwapDetailsDropdown
                  trade={trade}
                  cashbackPool={cashbackPool}
                  priceImpactWithoutFee={priceImpactWithoutFee}
                  realizedTradeFee={realizedTradeFee}
                  realizedLpFee={realizedLpFee}
                  tradeFee={tradeFee}
                />
              )
            ) : (
              <UnsupportedCurrencyFooter currencies={[currencies.INPUT, currencies.OUTPUT]} />
            )}
          </div>
        </Wrapper>
      </AppBody>

      <div className="mt-20 max-w-[570px] w-full">
        {chainId === ChainId.BSC ? <CashbackPoolWidget cashbackPool={cashbackPool} /> : <BinanceBridgeWidget />}
      </div>
    </Page>
  )
}
